import clsx from 'clsx'
import React, { ReactNode } from 'react'

import s from './styles.module.scss'

import HouseCardGalleryImage from './house-card-gallery-image/HouseCardGalleryImage'
import { IconSvg, Tag } from '../../atoms'
import { Capacity } from '../../molecules'
import { EventInformation } from '../../molecules/event-information/event-information'
import {
  HouseContext,
  HouseOptionsFragment,
  HousePictureFieldsFragment,
  HouseStartingFees,
  SiteLastEventInformationOutputDto,
  TagOutputDto,
} from '../../../lib/graphql'
import { cn } from '../../../shadcn/utils'

export interface IHouseCardGallery
  extends Pick<
    HouseContext,
    | '_id'
    | 'status'
    | 'address'
    | 'numberOfBeds'
    | 'numberOfBedrooms'
    | 'numberOfWorkspaces'
    | 'title'
    | 'capacity'
    | 'travelTime'
    | 'travelCity'
    | 'pathname'
    | 'companyName'
    | 'companyLogo'
    | 'lastEndDate'
    | 'isAutonomousBookingAvailable'
  > {
  options?: Pick<HouseOptionsFragment, 'studyDay' | 'corporateEvening'>
  coverPicture: HousePictureFieldsFragment
  tags?: Partial<TagOutputDto>[] | null
  startingFees?: number | HouseStartingFees | null

  eventInformation?: Pick<
    SiteLastEventInformationOutputDto,
    | 'travelType'
    | 'startDate'
    | 'endDate'
    | 'contactFirstname'
    | 'contactLastname'
  >
}

const HouseCardGallery = ({
  house,
  className,
  classNameDetails,
  searchMode,
  children,
  isExclusive = false,
  companyIcon,
  siteIcon,
}: {
  house: IHouseCardGallery
  className?: string
  classNameDetails?: string
  searchMode?: boolean
  children?: ReactNode
  isExclusive?: boolean
  companyIcon?: string
  siteIcon?: string
}) => {
  return (
    <article
      className={clsx(
        className,
        s.houseCardOta,
        'flex flex-col overflow-hidden rounded-[20px]',
        {
          [s.exclusive]: isExclusive,
        },
      )}
    >
      {isExclusive && (
        <Tag
          label='Executive'
          Icon={
            <IconSvg.RoomService
              style={{ width: 15, height: 15 }}
              color='black'
            />
          }
          className={s.badgeContainer}
        />
      )}

      <HouseCardGalleryImage
        house={house}
        className={clsx(s.image, { [s.searchMode]: searchMode })}
        companyIcon={companyIcon}
        hideAutonomousBookingTag={isExclusive}
        siteIcon={siteIcon}
      />

      <div
        className={clsx(s.informations, classNameDetails)}
        style={{
          padding: isExclusive
            ? '0 15px 30px 15px'
            : companyIcon
              ? '12px 12px 12px 12px'
              : '',
        }}
      >
        {!isExclusive && (
          <h3 className={cn(s.title, 'group-focus:underline')}>
            {house.title}
          </h3>
        )}
        <p className={cn(s.geolocation, 'line-clamp-1')}>
          {house?.address?.department &&
            house?.address?.region &&
            `${house?.address?.department}, ${house?.address?.region}`}
        </p>
        {isExclusive && (
          <h3 className={cn(s.title, 'group-focus:underline')}>
            {house.title}
          </h3>
        )}
        {!companyIcon && !siteIcon && (
          <Capacity
            numberOfWorkspaces={house.numberOfWorkspaces ?? 0}
            dailyMaxCapacity={house.options?.studyDay.options?.capacity.max}
            corporateEveningEventMaxCapacity={
              house.options?.corporateEvening.options?.capacity.max
            }
            beds={house.numberOfBeds ?? 0}
            bedrooms={house.numberOfBedrooms ?? 0}
            capacity={house.capacity ?? 0}
            className={s.capacity}
          />
        )}

        {!!house?.eventInformation && (
          <EventInformation {...house.eventInformation} />
        )}
      </div>

      {children}
    </article>
  )
}

export default HouseCardGallery
